import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { FC, ReactElement } from "react";

import { NavigationContainer } from "@react-navigation/native";
import type { LinkingOptions } from "@react-navigation/native";
import * as Linking from "expo-linking";
import { Box } from "native-base";
import { usePreloadedQuery, useQueryLoader } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import AuthNavigator from "pianofunclub-crm/navigation/AuthNavigator";
import type { AuthNavigatorParamList } from "pianofunclub-crm/navigation/AuthNavigator";
import HubNavigator from "pianofunclub-crm/navigation/HubNavigator";
import type { HubNavigatorParamList } from "pianofunclub-crm/navigation/HubNavigator";

import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import { useData } from "pianofunclub-shared/providers/DataProvider";
import type { LoadSchoolsForDropdownsQuery } from "pianofunclub-shared/relay/graphql/schools/__generated__/LoadSchoolsForDropdownsQuery.graphql";
import { load_schools_for_dropdowns } from "pianofunclub-shared/relay/graphql/schools/LoadSchoolsForDropdowns";
import LoadingScreen from "pianofunclub-shared/screens/LoadingScreen";
import WelcomeScreen from "pianofunclub-shared/screens/WelcomeScreen";

// flow adpated from: https://reactnavigation.org/docs/auth-flow/

const prefix = Linking.createURL("/");

interface Props {
  loadSchoolsForDropdownsQueryReference: PreloadedQuery<
    LoadSchoolsForDropdownsQuery,
    Record<string, unknown>
  >;
}

// disable in dev
const SPLASH_SCREEN_TIME = !__DEV__ ? 4000 : 0; // in ms

const AppNavigator = (props: Props): ReactElement => {
  const { loadSchoolsForDropdownsQueryReference } = props;

  const [showSplash, setShowSplash] = useState(true);

  const { authState } = useAuth();

  const { dispatchDataState } = useData();

  const schoolsData = usePreloadedQuery<LoadSchoolsForDropdownsQuery>(
    load_schools_for_dropdowns,
    loadSchoolsForDropdownsQueryReference
  );

  useEffect(() => {
    dispatchDataState({ input: "schools", value: schoolsData?.schools });
  }, [dispatchDataState, schoolsData?.schools]);

  useEffect(() => {
    if (window.devicePixelRatio > 1) {
      alert("Your screen may appear zoomed in. For best results, press ⌘ + - to zoom out.");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, SPLASH_SCREEN_TIME);

    return () => clearTimeout(timer);
  }, []);

  const navRef = useRef(null);

  const linking: LinkingOptions<HubNavigatorParamList & AuthNavigatorParamList> = useMemo(() => {
    return {
      config: {
        screens: {
          AccountsHubTab: {
            initialRouteName: "AccountsHub",
            screens: {
              Account: "accounts/account",
              AccountsHub: "accounts",
              DefaultTermDates: "accounts/default-term-dates",
              Enrolment: "accounts/enrolment",
              Invoice: "accounts/invoice",
              School: "accounts/school",
            },
          },
          ForgotPassword: "forgot-password",
          InvoicingHubTab: {
            initialRouteName: "InvoicingHub",
            screens: {
              Account: "invoicing/account",
              DefaultTermDates: "invoicing/default-term-dates",
              Enrolment: "invoicing/enrolment",
              Invoice: "invoicing/invoice",
              InvoicingHub: "invoicing",
              School: "invoicing/school",
            },
          },
          Login: "login",
          PayrollHubTab: {
            initialRouteName: "PayrollHub",
            screens: {
              Account: "partner-payouts/account",
              DefaultTermDates: "partner-payouts/default-term-dates",
              Enrolment: "partner-payouts/enrolment",
              Invoice: "partner-payouts/invoice",
              Payrates: "partner-payouts/payrates",
              PayrollHub: "partner-payouts",
              Payslip: "partner-payouts/payout",
              School: "partner-payouts/school",
            },
          },
          ProfileHubTab: {
            initialRouteName: "ProfileHub",
            screens: {
              ChangeName: "settings/change-name",
              ChangePassword: "settings/change-password",
              ProfileHub: "settings",
              UpdateEmail: "settings/update-email",
            },
          },
          Register: "register",
          RegistersHubTab: {
            initialRouteName: "RegistersHub",
            screens: {
              Account: "registers/account",
              DefaultTermDates: "registers/default-term-dates",
              Enrolment: "registers/enrolment",
              Invoice: "registers/invoice",
              RegistersHub: "registers",
              School: "registers/school",
            },
          },
          ResetPassword: "reset-password",
          SchoolsHubTab: {
            initialRouteName: "SchoolsHub",
            screens: {
              Account: "schools/account",
              DefaultTermDates: "schools/default-term-dates",
              Enrolment: "schools/enrolment",
              Invoice: "schools/invoice",
              School: "schools/school",
              SchoolsHub: "schools",
            },
          },
          TeacherRegistersHubTab: {
            initialRouteName: "TeacherRegistersHub",
            screens: {
              TeacherRegistersHub: "teacher-registers",
            },
          },
          TeacherTimetableHubTab: {
            initialRouteName: "TeacherTimetableHub",
            screens: {
              TeacherTimetableHub: "teacher-timetable",
            },
          },
          WaitingListHubTab: {
            initialRouteName: "WaitingListHub",
            screens: {
              Account: "waiting-list/account",
              DefaultTermDates: "waiting-list/default-term-dates",
              Enrolment: "waiting-list/enrolment",
              Invoice: "waiting-list/invoice",
              School: "waiting-list/school",
              WaitingListHub: "waiting-list",
            },
          },
        },
      },
      prefixes: [prefix],
      subscribe(listener) {
        const onReceiveURL = ({ url }: { url: string }) => {
          listener(url);
        };

        // Listen to incoming links
        const linkingSubscription = Linking.addEventListener("url", onReceiveURL);
        return () => {
          // Clean up the event listeners
          linkingSubscription.remove();
        };
      },
    };
  }, []);

  if (showSplash) {
    return (
      <Box flex={1}>
        <WelcomeScreen />
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <NavigationContainer
        ref={navRef}
        documentTitle={{
          formatter: (options) =>
            `PianoFunClub${options?.headerTitle ? " | " + options.headerTitle : ""}`,
        }}
        linking={linking}
      >
        {authState.userToken ? <HubNavigator /> : <AuthNavigator />}
      </NavigationContainer>
    </Box>
  );
};

const AppNavigatorWrapper: FC = () => {
  const [loadSchoolsForDropdownsQueryReference, loadSchoolsForDropdownsQuery] =
    useQueryLoader<LoadSchoolsForDropdownsQuery>(load_schools_for_dropdowns);

  useEffect(() => {
    loadSchoolsForDropdownsQuery({ orderBy: "archived,name" }, { fetchPolicy: "network-only" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loadSchoolsForDropdownsQueryReference) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <AppNavigator loadSchoolsForDropdownsQueryReference={loadSchoolsForDropdownsQueryReference} />
    </Suspense>
  );
};

export default AppNavigatorWrapper;
